/**
 * Every `code` the partner API emits (contracts/partner-api.md, "Error codes
 * as shipped"), mapped to the sentence a partner sees. Clients branch on
 * `code`; the server's `error` slug is prose for logs and is never shown.
 *
 * `ERROR_MESSAGES` is exhaustive on purpose: `errors.test.ts` walks the
 * contract's table and fails if a shipped code has no human message here.
 */
export const ERROR_MESSAGES = {
  // Surface-wide
  UNSUPPORTED_API_VERSION:
    'This version of the portal is out of date for the API. Reload the page to get the current one.',
  INVALID_CURSOR: 'That page of results is no longer valid. Start again from the first page.',
  UNAUTHENTICATED: 'Please sign in to continue.',
  INVALID_TOKEN: 'Your session has expired. Sign in again to continue.',
  SESSION_REVOKED: 'You were signed out, either from another device or by an admin. Sign in again.',
  MEMBERSHIP_REQUIRED: 'You are no longer a member of this organisation.',
  ACCOUNT_DISABLED: 'This account has been disabled. Contact your organisation’s owner.',
  ORG_SELECTION_REQUIRED: 'Choose an organisation before continuing.',
  ROLE_FORBIDDEN: 'Your role does not allow this. Ask an owner or admin.',
  NOT_FOUND: 'We could not find that. It may have been removed.',
  // FinderlyFix operator console (/ops, migration 076).
  CONFIRMATION_MISMATCH: 'The confirmation did not match this organisation. Reload the page and try again.',
  CONFIRM_SLUG_MISMATCH: 'The name you typed does not match. Type it exactly as shown.',
  SITE_RELEASE_NOT_ELIGIBLE: 'Only a disabled, suspended, reported or offboarded organisation’s website can be released.',
  SITE_ALREADY_RELEASED: 'This organisation’s website was already released.',
  PARTNER_OFFBOARDING: 'This organisation has an offboarding case or was offboarded, so it cannot be approved. Cancel the case first.',
  PARTNER_AUTH_UNAVAILABLE: 'Partner sign-in is not configured on this server.',
  PARTNER_AUTH_MISSING: 'Something went wrong with your session. Sign in again.',
  INTERNAL_ERROR: 'Something went wrong on our side. Try again in a moment.',

  // Auth
  OTP_COOLDOWN: 'We just sent a code. Wait a minute before asking for another.',
  OTP_DAILY_LIMIT: 'Too many codes requested for this email today. Try again tomorrow.',
  OTP_LOCKED: 'Too many attempts. Wait a few minutes and try again.',
  OTP_UNAVAILABLE: 'Sign-in is briefly unavailable. Try again in 30 seconds.',
  OTP_INVALID: 'That code is not right, or it has expired. Check the latest email.',
  TOKEN_EXPIRED: 'Your session has expired. Sign in again.',
  GOOGLE_SIGNIN_DISABLED: 'Google sign-in isn’t available right now. Use your work email instead.',
  GOOGLE_TOKEN_INVALID: 'Google sign-in didn’t go through. Try again, or use your work email.',
  GOOGLE_EMAIL_UNVERIFIED:
    'Google hasn’t verified the email on that account. Use your work email and we’ll send you a code.',
  GOOGLE_ACCOUNT_MISMATCH:
    'This email is linked to a different Google account. Sign in with that account, or use your work email.',
  EMAIL_TAKEN:
    'That email already signs in to another FinderlyFix partner account. Sign in with it there, or ask whoever invited it to remove it first.',
  EMAIL_UNCHANGED: 'That is already your sign-in email.',
  // Migration 093: extra addresses for AI agent and security notices.
  NOTICE_RECIPIENT_LIMIT: 'You can add up to 3 addresses. Remove one first.',
  NOTICE_RECIPIENT_EXISTS: 'That address is already on the list.',
  NOTICE_RECIPIENT_IS_MEMBER: 'That address belongs to an owner or admin here, who already gets these notices.',
  // Security audit 2026-09-28 (migration 086).
  // Link a Google account while signed in (migration 090).
  GOOGLE_ACCOUNT_IN_USE:
    'That Google account already signs in to another FinderlyFix partner account. Pick a different one, or unlink it there first.',
  GOOGLE_ALREADY_LINKED: 'A Google account is already linked. Unlink it first to link a different one.',
  GOOGLE_LINK_REVERT_LINK_INVALID:
    'This link doesn’t work: it may have expired (links last 14 days) or been changed. Contact support@finderlyfix.com and we’ll help you secure the account.',
  STEP_UP_REQUIRED: 'For your security, confirm it’s you first with a code sent to your current sign-in email.',
  OTP_GLOBAL_LIMIT: 'We’re sending a lot of sign-in codes right now. Try again in a few minutes.',
  EMAIL_REVERT_LINK_INVALID:
    'This link doesn’t work: it may have expired (links last 14 days) or been changed. Contact support@finderlyfix.com and we’ll help you secure the account.',
  REFRESH_REUSE_DETECTED:
    'For your security we signed you out everywhere, because an old session token was reused.',

  // Org / members
  LAST_OWNER: 'An organisation needs at least one owner. Make someone else an owner first.',
  ALREADY_MEMBER: 'That person is already a member of this organisation.',
  CONFIRM_NAME_MISMATCH: 'The name you typed does not match your organisation’s name. Type it exactly as shown.',
  TERMS_OUTDATED: 'The Partner Terms were updated while this page was open. Reload the page and review the current version.',

  // Sites
  ORIGIN_ALREADY_REGISTERED:
    'That origin is already on this site, or another site has verified it.',
  ORIGIN_NOT_HTTPS: 'Origins must start with https:// (for example https://www.example.com).',
  ORIGIN_RESERVED:
    'FinderlyFix addresses (finderlyfix.com and its subdomains) can’t be added as your website. Your hosted page works without it.',
  VERIFY_URL_NOT_ALLOWED: 'That page can’t be checked yet.',
  ORIGIN_LIST_EMPTY:
    'Your list has no allowed websites yet, so locking it would switch the widget off everywhere. Use “Add to list” on your own website below first.',

  // Configs
  CONTRAST_TOO_LOW: 'Some colour pairs are too low in contrast to be readable.',
  STALE_VERSION: 'A newer draft exists. Review the latest draft before publishing.',
  NOTHING_PUBLISHED: 'Nothing has been published yet, so there is no live version to go back to. Edit the draft instead.',
  NO_PAIRED_TEST_SITE: 'This site has no test key to preview against.',
  // PR #864 (placement.size) and PR #865 (routing.actions): 400 with
  // `failing_fields`, rendered next to the field each entry names.
  SIZE_OUT_OF_RANGE: 'Some card-size values are out of range.',
  INVALID_ROUTING_ACTIONS: 'Some result-screen actions are not valid.',

  // Destinations
  SMS_CAMPAIGN_NOT_REGISTERED:
    'SMS is not available until your 10DLC campaign is registered.',
  DESTINATION_URL_BLOCKED:
    'That webhook URL is not allowed. It must be a public https:// address on port 443.',
  INVALID_CONFIG: 'Some of those destination settings are not valid.',
  DESTINATION_KIND_UNSUPPORTED: 'That setting cannot be changed for this kind of destination.',
  DESTINATION_NOT_SIGNED: 'Only webhook destinations have a signing secret to rotate.',
  DESTINATION_DISABLED: 'This destination is disabled. Enable it before sending a test event.',
  DELIVERY_ENQUEUE_FAILED: 'The test event could not be queued. Try again shortly.',

  // Leads
  DESTINATION_REMOVED: 'That destination has been removed, so the delivery cannot be resent.',
  VENDOR_UNAVAILABLE: 'That contractor is not available for this lead.',
  VENDOR_ALREADY_ASSIGNED: 'That contractor is already assigned to this lead.',
  LEAD_ALREADY_CLOSED: 'This lead is already closed. Reload to see its outcome, or reopen it first.',
  LEAD_NOT_CLOSED: 'This lead is not closed, so there is nothing to reopen.',

  // Dashboard
  INVALID_VENDOR: 'Some of this contractor’s details need fixing.',
  EXTERNAL_ID_TAKEN: 'Another contractor already uses that ID. IDs must be unique on your roster.',
  // Contractor opt-outs (migration 084)
  RECONSENT_REQUIRED: 'Only the contractor can lift their opt-out. Record what they told you, or email them their own re-subscribe link.',
  VENDOR_NOT_OPTED_OUT: 'This contractor is not opted out, so there is nothing to lift.',
  RECONSENT_BEFORE_OPT_OUT: 'The contractor opted out after that date. Record when they asked for offers again, after their latest opt-out.',
  INVALID_RECEIVED_AT: 'That date is in the future. Enter when the contractor told you.',
  VENDOR_CONTACT_CHANGED: 'The contractor’s phone or email changed after this was recorded, so it was not applied.',
  VENDOR_NO_EMAIL: 'This contractor has no email on file to send the link to.',
  RESUBSCRIBE_LINK_RECENT: 'A re-subscribe link was sent to this contractor in the last hour. Ask them to check their inbox.',
  EMAIL_SEND_FAILED: 'The email could not be sent. Try again in a minute.',
  INVALID_CSV: 'We could not read that file as a contractor roster.',
  CSV_TOO_LARGE: 'That file has more than 1,000 contractors. Split it and import each part.',
  INVALID_ROUTING_RULES: 'A rule names a contractor who is not on your roster.',

  INVALID_PERIOD: 'That date range is not valid.',
  PERIOD_TOO_LONG: 'Choose a range of 90 days or less.',
  SHARE_LINK_LIMIT: 'You have 20 active share links. Revoke one before creating another.',

  // Hosted page
  NO_LIVE_SITE: 'Your hosted page needs a live site key, and this organisation has none. Create a site first.',
  SITE_NOT_LIVE: 'A hosted page can only use a live key; test keys cannot collect real leads.',
  SITE_DISABLED: 'That site key is disabled. Enable it or choose another.',
  SLUG_TAKEN: 'That page address is already taken. Try another.',
  SLUG_IMMUTABLE: 'Your page address is already set and cannot be changed.',
  SLUG_RESERVED: 'That page address is reserved. Try another, such as your business name.',
  HOSTED_PAGE_LIMIT: 'You already have 3 pages. Remove one to add another.',
  HOSTED_PAGE_NOT_FOUND: 'That widget page no longer exists. Reload to see your pages.',
  // Hosted page profile and photos (migration 100)
  LOGO_ASSET_INVALID: 'That logo is no longer on this page. Upload it again, then save.',
  FILE_REQUIRED: 'Choose a photo to upload.',
  INVALID_UPLOAD: 'The upload did not come through. Try choosing the photo again.',
  GALLERY_FULL: 'Your page already has 12 photos. Remove one to add another.',
  ASSET_TOO_LARGE: 'That photo is too large. Use one under 8 MB.',
  IMAGE_INVALID: 'That file is not a photo we can use. Upload a JPEG, PNG or WebP image.',
  ASSET_STORE_UNAVAILABLE: 'Photos can’t be saved right now. Try again in a few minutes.',
  ASSET_NOT_FOUND: 'That photo is no longer on this page. Reload to see the current photos.',
  // Hosted page quote form (the visitor's browser posts it, not the portal)
  INVALID_REQUEST: 'Some of the quote request’s details are missing or not valid.',
  ORIGIN_MISMATCH: 'The quote request did not come from the contractor’s FinderlyFix page.',
  QUOTE_FORM_DISABLED: 'The quote form on this page is switched off, or the widget settings do not allow it.',
  LEAD_REFUSED: 'The quote request could not be filed as a lead.',
  INTAKE_UNAVAILABLE: 'The quote request could not be sent right now. Try again in a moment.',

  // Billing (migration 101). The first four match billing/errors.ts, which
  // the billing screens used before these codes were listed in the contract.
  FEE_SCHEDULE_OUTDATED:
    'The Contractor Fee Schedule was updated while this page was open. Reload the page and review the current version.',
  FEE_SCHEDULE_REQUIRED:
    'An owner or admin needs to accept the Contractor Fee Schedule on the Billing page before leads can be accepted.',
  LEAD_NOT_ACCEPTABLE:
    'This lead can no longer be accepted here: it may have been closed, expired or already accepted. Reload the page to see where it stands.',
  BILLING_NOT_ENABLED: 'Billing isn’t switched on for your organisation yet.',
  AMOUNT_SIGN_INVALID: 'Credits and payments are entered as negative amounts; an adjustment cannot be zero.',
  LEAD_NOT_FOUND: 'That lead does not belong to this organisation.',
  ENTRY_NOT_FOUND: 'That ledger entry does not belong to this organisation.',
  NO_UNINVOICED_ENTRIES: 'There is nothing to invoice for that period.',
  INVOICE_NOT_DRAFT: 'This invoice has already been issued or voided.',
  INVOICE_NOT_ISSUED: 'Only an issued invoice can be marked paid.',
  INVOICE_NOT_VOIDABLE: 'This invoice is paid or already void, so it cannot be voided.',

  // Quotes and projects (migration 102)
  QUOTES_DISABLED: 'Quotes are not available yet.',
  LEAD_NOT_ACCEPTED: 'Accept this lead before sending a quote.',
  CONTACT_EMAIL_MISSING: 'The homeowner did not give an email address, so a quote cannot be sent to them.',
  QUOTE_OPEN_EXISTS: 'This lead already has an open quote. Withdraw it before writing a new one.',
  PROJECT_EXISTS: 'The homeowner already accepted a quote for this lead.',
  LINE_ITEMS_MISMATCH: 'The line items must add up to the quote total.',
  QUOTE_NOT_DRAFT: 'This quote has already been sent, so it can no longer be changed.',
  QUOTE_NOT_OPEN: 'This quote is no longer open, so there is nothing to withdraw.',
  PROJECT_NOT_STARTABLE: 'This job has already started or ended.',
  PROJECT_NOT_COMPLETABLE: 'This job is already marked complete, or it was cancelled.',
  PROJECT_NOT_CANCELLABLE: 'This job can no longer be cancelled here. Contact support@finderlyfix.com.',
  PROJECT_OPEN: 'This lead has a job in progress. Finish or cancel the job before closing the lead.',
  PROJECT_ALREADY_CONFIRMED: 'This job is already confirmed complete.',
  PROJECT_NOT_RESOLVABLE: 'This job is not waiting for a decision. Reload to see where it stands.',
  FINAL_VALUE_REQUIRED: 'Enter the adjusted job value.',

  // Agent access (migration 070). Most of these reach an AI agent, not the
  // portal; they are here so the contract's table stays exhaustive.
  AGENT_TOKEN_INVALID: 'That agent token is unknown, expired or revoked. Create a new one on AI agent access.',
  AGENT_TOKEN_CREATOR_INACTIVE: 'The person who created that agent token is no longer an active member.',
  AGENT_HUMAN_ONLY: 'Only a person can do this, from the portal.',
  AGENT_ROUTE_NOT_ALLOWED: 'Agent tokens cannot use this part of the API.',
  AGENT_SCOPE_REQUIRED: 'That agent token is read-only. Create a “Read and set up” one.',
  AGENT_SITE_SCOPE: 'That agent token is limited to specific sites and cannot change organisation-wide settings.',
  AGENT_HUMAN_ONLY_FIELD: 'Only a person can change some of these settings, from the portal.',
  AGENT_DELAYED_CHANGE: 'An AI agent makes this change another way: it takes effect at once, and owners and admins are emailed with an Undo link.',
  VENDOR_ALREADY_ACTIVE: 'Those contractors are already active.',
  AGENT_HUMAN_ONLY_PUBLISH: 'This version includes a change only a person can publish. Publish it from Widget settings.',
  AGENT_ONLY: 'This endpoint is for AI agent tokens, not portal sessions.',
  // Agent sign-up and the claim (migration 080).
  AGENT_CLAIM_REQUIRED:
    'An AI agent can’t do this until the person it named claims the account: they sign in with that email and confirm.',
  CLAIM_CONTACT_ONLY:
    'Only the person the AI agent named can claim this account. Ask them to sign in with that email and confirm.',
  CLAIM_NOT_APPLICABLE: 'This organisation wasn’t set up by an AI agent, so there is nothing to claim.',
  CLAIM_EMAIL_UNVERIFIED: 'Sign in again with your email code first, so we know the address is yours.',
  SIGNUP_REPORT_LINK_INVALID:
    'This link isn’t valid any more. Use the link in the latest email, or contact support@finderlyfix.com.',
  SIGNUP_RATE_LIMITED: 'Too many attempts from here. Wait a few minutes and try again.',
  // Migration 083: team invitations an AI agent asked for.
  INVITE_LINK_INVALID: 'This invitation link isn’t valid. Use the link in the latest email, or ask the organisation to invite you again.',
  INVITE_ALREADY_ACCEPTED: 'This invitation was already accepted. Sign in with the invited email.',
  INVITE_REVOKED: 'The organisation withdrew this invitation.',
  INVITE_EXPIRED: 'This invitation has expired. Ask the organisation to invite you again.',
  INVITE_UNAVAILABLE: 'This organisation can’t take new members right now. Contact support@finderlyfix.com if you think that’s wrong.',
  INVITE_RATE_LIMITED: 'Too many attempts from here. Wait a few minutes and try again.',
  // The agent sign-up API (migration 080). An agent reads these; a person may
  // see them relayed.
  SIGNUP_ORIGIN_INVALID: 'That website address can’t be signed up. Use its https:// address on the standard port, with a hostname.',
  SIGNUP_CHALLENGE_INVALID: 'That sign-up challenge has expired, was already used, or ran out of attempts. Start again with a new challenge.',
  HOST_HAS_UNCLAIMED_ORG: 'An AI agent already set up an account for this website that nobody has claimed yet. The person it named can claim it from the email they were sent.',
  ORIGIN_TAKEN: 'Another FinderlyFix organisation already verified this website. Ask its owner to add you, or contact support@finderlyfix.com.',
  SIGNUP_PROOF_NOT_FOUND: 'We couldn’t find the verification line on the site or the DNS record yet. Check it is published, then try again.',
  SIGNUP_CONTACT_LIMIT: 'That person already has three accounts waiting to be claimed. Ask them to claim one first.',
  SIGNUP_UNAVAILABLE: 'Sign-up is briefly unavailable. Try again in a minute.',
  SIGNUP_CONTACT_EXISTING_USER:
    'This person already has a FinderlyFix account — sign in and create the organisation in the portal, or name a different contact.',
  SIGNUP_ALREADY_CLAIMED: 'This account has been claimed, so this link no longer does anything. Contact support@finderlyfix.com if that is wrong.',
  AGENT_TOKEN_LIMIT: 'You have 20 active agent tokens. Revoke one before creating another.',
  OAUTH_REQUEST_NOT_FOUND: 'This sign-in request has expired or was already answered. Start the sign-in again from your terminal.',
  OAUTH_SCOPE_NOT_REQUESTED: 'You can give the agent less access than it asked for, not more.',
  PENDING_CHANGE_LIMIT: 'There are 20 agent requests waiting for approval. Approve or reject some first.',
  PENDING_CHANGE_DECIDED: 'This change was already approved, rejected or has expired.',
  // Migration 096: Undo, and the emailed Undo / Approve / Reject links.
  ALREADY_UNDONE: 'This change was already undone.',
  UNDO_UNAVAILABLE: 'This change can’t be undone with one click any more. Change it back by hand on the page it changed.',
  CHANGED_SINCE: 'This was changed again after the agent’s change, so Undo would overwrite that. Change it by hand instead.',
  PENDING_CHANGE_NEEDS_DECISION: 'This change is waiting for approval: approve or reject it instead.',
  AGENT_CHANGE_LINK_INVALID:
    'This link doesn’t work: it may have expired or been changed. Sign in and use the top of your dashboard instead.',
  NO_TEST_KEY: 'This site has no test key, so no test lead can be sent.',
  TEST_LEAD_REFUSED: 'The widget refused the test lead. The reason is in the details.',
  METHOD_NOT_ALLOWED: 'That method is not supported here.',
  AGENT_TOKEN_TERMS_REQUIRED:
    'Your organisation has not accepted the current Partner Terms, which cover agent tokens. An owner or admin accepts them under Organisation → Partner Terms → Review and accept.',
  CONTACT_RULES_ATTESTATION_REQUIRED:
    'An AI agent added or changed some of these contractors. Tick the contact-rules confirmation to activate them.',

  // Widget intake (seen on the "test a lead" screen)
  WIDGET_CONFIG_UNAVAILABLE: 'The widget could not read its published configuration.',
  WIDGET_LEAD_NOT_AVAILABLE: 'Leads are not enabled for this site key.',
  WIDGET_CONSENT_REQUIRED: 'The visitor did not agree to be contacted.',
  WIDGET_LEAD_ROUTING_DISABLED:
    'The published routing mode does not collect contact details, so no lead was filed.',
  WIDGET_CONTEXT_TOKEN_REQUIRED: 'This site requires a signed context token, and none was sent.',
  WIDGET_CONTEXT_TOKEN_INVALID: 'The context token was refused (bad signature or already used).',
  WIDGET_CONTACT_INCOMPLETE: 'Required contact fields were missing.',
  WIDGET_CONTACT_RECONFIRM:
    'This phone number previously asked not to be contacted, so the visitor must confirm contact for this request before it is sent.',
  WIDGET_LEAD_OUT_OF_SCOPE: 'That trade is outside the configured scope.',
  WIDGET_LEAD_OUT_OF_AREA: 'That postal code is outside the configured service area.',
  WIDGET_LEAD_LOW_CONFIDENCE: 'The diagnosis confidence was below the configured threshold.',
  WIDGET_LEAD_UNAVAILABLE: 'The lead could not be saved. Try again.',
  WIDGET_NO_ROSTER_PHONE: 'No contractor phone number matched.',
  WIDGET_ROSTER_UNAVAILABLE: 'The contractor roster could not be read.',
  WIDGET_ATTACH_TOKEN_INVALID: 'This photo could not be matched to the request it was meant for.',
  WIDGET_ATTACH_TOKEN_EXPIRED: 'It is too late to add a photo to this request.',
  WIDGET_MEDIA_NOT_OWNED: 'That photo was not uploaded by this visitor on this site.',
  WIDGET_LEAD_NOT_FOUND: 'That lead was not found for this site.',
  WIDGET_LEAD_CLOSED: 'This lead is closed, so a photo can no longer be added.',
  WIDGET_LEAD_MEDIA_LIMIT: 'This lead already has the 2 photos a homeowner can add after sending.',
  WIDGET_MEDIA_COPY_FAILED: 'The photo could not be copied to the lead. Try again.',

  // Client-side codes (never sent by the server)
  NETWORK_ERROR: 'We could not reach FinderlyFix. Check your connection and try again.',
  VALIDATION_FAILED: 'Some fields need attention.',
  RATE_LIMITED: 'Too many requests. Wait a moment and try again.',
  // Widget usage limits (migration 089)
  LIMIT_NOT_AN_INCREASE: 'Each limit you ask for must be higher than the current one.',
  LIMIT_REQUEST_PENDING: 'A request for this site is already waiting. Withdraw it to ask for something else.',
  NO_LIMIT_REQUESTED: 'Enter at least one new limit.',
  LIMIT_REQUEST_DECIDED: 'This request was already decided.',
  NO_LIMIT_GRANTED: 'Enter at least one limit to grant.',
} as const;

export type ErrorCode = keyof typeof ERROR_MESSAGES;

export function isKnownCode(code: string): code is ErrorCode {
  return Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, code);
}

/** A field-level problem, from a 400 zod body or a 409's structured extra. */
export interface FieldIssue {
  /** Dotted path, e.g. `branding.colors.primary` or `config.url`. */
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string | null;
  readonly fields: FieldIssue[];
  readonly body: Record<string, unknown>;

  constructor(args: {
    status: number;
    code: string;
    message?: string;
    correlationId?: string | null;
    fields?: FieldIssue[];
    body?: Record<string, unknown>;
  }) {
    super(args.message ?? humanMessage(args.code));
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.correlationId = args.correlationId ?? null;
    this.fields = args.fields ?? [];
    this.body = args.body ?? {};
  }

  /** Builds an ApiError from any non-2xx response body the partner API sends. */
  static fromResponse(status: number, body: unknown): ApiError {
    const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
    const correlationId = typeof record.correlationId === 'string' ? record.correlationId : null;
    const rawCode = typeof record.code === 'string' ? record.code : null;

    // middleware/validate.ts's 400 carries no `code`: { error, source, message }.
    // `message` is "path: reason; path: reason", which is the only field-level
    // detail it sends in production.
    if (!rawCode && status === 400) {
      const fields = parseZodMessage(typeof record.message === 'string' ? record.message : '');
      return new ApiError({ status, code: 'VALIDATION_FAILED', correlationId, fields, body: record });
    }

    const code = rawCode ?? (status === 429 ? 'RATE_LIMITED' : status >= 500 ? 'INTERNAL_ERROR' : 'UNKNOWN');
    const fields = structuredFields(code, record);
    let message = humanMessage(code);
    // VERIFY_URL_NOT_ALLOWED and DESTINATION_URL_BLOCKED carry a
    // public-safe explanation; prefer it when present.
    if (code === 'VERIFY_URL_NOT_ALLOWED' && typeof record.message === 'string') {
      message = `${message} ${record.message}`;
    }
    if (code === 'DESTINATION_URL_BLOCKED' && typeof record.error === 'string' && record.error.includes(' ')) {
      message = `${message} (${record.error})`;
    }
    if (code === 'OTP_COOLDOWN' && typeof record.retryAfterSec === 'number') {
      message = `We just sent a code. You can ask for another in ${record.retryAfterSec} s.`;
    }
    return new ApiError({ status, code, message, correlationId, fields, body: record });
  }
}

export function humanMessage(code: string): string {
  return isKnownCode(code) ? ERROR_MESSAGES[code] : 'Something unexpected happened. Try again.';
}

/** `branding.colors.primary: must be a #rgb colour; config.url: Required` → issues. */
export function parseZodMessage(message: string): FieldIssue[] {
  if (!message) return [];
  return message
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.indexOf(':');
      if (idx <= 0) return { path: '', message: part };
      return { path: part.slice(0, idx).trim(), message: part.slice(idx + 1).trim() };
    });
}

const CONTRAST_PAIR_FIELDS: Record<string, string> = {
  text_on_surface: 'branding.colors.text',
  on_primary_on_primary: 'branding.colors.on_primary',
  primary_on_surface: 'branding.colors.primary',
};

const CONTRAST_PAIR_LABELS: Record<string, string> = {
  text_on_surface: 'Text on surface',
  on_primary_on_primary: 'Button text on primary',
  primary_on_surface: 'Primary on surface',
};

/** Turns the structured extras on 409s (and INVALID_CONFIG) into field issues. */
function structuredFields(code: string, record: Record<string, unknown>): FieldIssue[] {
  if (code === 'CONTRAST_TOO_LOW' && Array.isArray(record.failing_pairs)) {
    return record.failing_pairs.flatMap((raw) => {
      if (!raw || typeof raw !== 'object') return [];
      const pair = raw as { pair?: string; ratio?: number; required?: number };
      const key = pair.pair ?? '';
      return [
        {
          path: CONTRAST_PAIR_FIELDS[key] ?? 'branding.colors',
          message: `${CONTRAST_PAIR_LABELS[key] ?? key} is ${pair.ratio}:1; it needs at least ${pair.required}:1.`,
        },
      ];
    });
  }
  // SIZE_OUT_OF_RANGE and INVALID_ROUTING_ACTIONS (both 400, PRs #864/#865,
  // unified by the orchestrator) and any future config refusal share one
  // shape: failing_fields[{ field, value, min?, max?, allowed?, message }].
  if (Array.isArray(record.failing_fields)) {
    return record.failing_fields.flatMap((raw) => {
      if (!raw || typeof raw !== 'object') return [];
      const f = raw as { field?: string; message?: string; min?: number; max?: number; allowed?: unknown[] };
      const range = f.allowed
        ? `Must be one of ${f.allowed.join(', ')}.`
        : f.min !== undefined && f.max !== undefined
          ? `Must be between ${f.min} and ${f.max}.`
          : 'Not a valid value.';
      return [{ path: f.field ?? '', message: f.message ?? range }];
    });
  }
  if (code === 'INVALID_CONFIG' && Array.isArray(record.details)) {
    return record.details.flatMap((raw) => {
      if (!raw || typeof raw !== 'object') return [];
      const d = raw as { path?: string; message?: string };
      return [{ path: `config.${d.path ?? ''}`, message: d.message ?? 'Invalid value' }];
    });
  }
  return [];
}
